import type {
  ExtendedPropertyFieldRule,
  PropertyFieldValue,
} from "@survey-types/survey.comparison.types";
import type { Property } from "@survey-types/survey.property.types";
import type { ReactNode } from "react";
import { ContentSplitterUtils } from "./paginationUtils";

/**
 * Utility functions for optimizing property comparison performance
 *
 * React Compiler Optimization:
 * - Functions are automatically optimized by React compiler
 * - No manual memoization required
 * - Pure functions for predictable compiler optimization
 */

/**
 * Splits a value into an array of lines when it has no spaces and exceeds charsPerLine.
 * Returns the original string if no splitting is needed.
 */
const splitIfNoSpaceExceedsLimit = (
  value: string,
  charsPerLine: number,
): string | string[] => {
  if (ContentSplitterUtils.isUrlOrHtmlPattern(value)) {
    return value;
  }
  const hasNoSpaces = !value.includes(" ") && !value.includes("\n");
  if (hasNoSpaces && value.length > charsPerLine) {
    const lines: string[] = [];
    for (let i = 0; i < value.length; i += charsPerLine) {
      lines.push(value.substring(i, i + charsPerLine) + "\n");
    }
    return lines;
  }
  return value;
};

/**
 * Extracts and processes a field value from a property object
 * Handles standard fields, custom fields, and split content
 */
export const extractPropertyFieldValue = (
  property: Property,
  field: ExtendedPropertyFieldRule,
  charsPerLine = 25, // Default character limit for splitting
): ReactNode => {
  const { fieldKey, fieldId, splitContentMap, isContinued } = field;

  // Check if we have split content for this property
  if (splitContentMap && splitContentMap.has(property.propertyId)) {
    const splitLines = splitContentMap.get(property.propertyId) || [];
    // Return the split lines as an array for the component to render
    return splitLines;
  }

  // For continued fields, if this property doesn't have split content, show dash
  if (isContinued && splitContentMap) {
    return "—";
  }

  // If fieldKey is null or undefined, this is a custom field
  if (fieldKey == null && fieldId) {
    const customField = property.customFields?.find(
      (cf) => cf.customField.customFieldId === fieldId,
    );
    const customValue = customField?.value;
    return customValue
      ? splitIfNoSpaceExceedsLimit(customValue, charsPerLine)
      : "—";
  }

  // Special handling for name field: Display Property Name or Address Line 1
  if (fieldKey === "name") {
    const name = property.name;
    const address1 = property.address1;
    // If name is "Untitled Property" or empty, use address1
    if (!name || name === "Untitled Property") {
      return address1
        ? splitIfNoSpaceExceedsLimit(address1, charsPerLine)
        : "—";
    }
    return splitIfNoSpaceExceedsLimit(name, charsPerLine);
  }

  // Fallback to standard property field
  if (fieldKey) {
    const value = property[fieldKey as keyof Property];
    return value
      ? splitIfNoSpaceExceedsLimit(String(value), charsPerLine)
      : "—";
  }

  return "—";
};

/**
 * Pre-processes all property values for a specific field
 * Returns an optimized array of PropertyFieldValue objects
 */
export const preprocessPropertyFieldValues = (
  properties: Property[],
  field: ExtendedPropertyFieldRule,
  availableAddressFields?: string[],
  isNameFieldVisible = true,
  charsPerLine = 25, // Default character limit for splitting
): PropertyFieldValue[] => {
  return properties.map((property) => {
    let value: ReactNode;
    let isEmpty: boolean;

    // Special handling for address1 field - optimize by handling it first
    if (field?.fieldKey === "address1") {
      // Only exclude address line 1 if:
      // 1. Name field IS visible AND
      // 2. Name is empty or "Untitled Property" (so address1 is shown in name field)
      const shouldExcludeAddressLine1 =
        isNameFieldVisible &&
        (!property.name || property.name === "Untitled Property");

      value =
        getFullAddressDetails(
          property,
          availableAddressFields,
          shouldExcludeAddressLine1,
        ) || "—";
      isEmpty = value === "—" || value === "" || value == null;
    } else {
      // For all other fields, use standard extraction
      value = extractPropertyFieldValue(property, field, charsPerLine);
      isEmpty = value === "—" || value === "" || value == null;
    }

    return {
      propertyId: property.propertyId,
      value,
      isEmpty,
    };
  });
};

export const getFullAddressDetails = (
  property: Property,
  availableAddressFields?: string[],
  excludeAddressLine1 = false,
): string => {
  if (!availableAddressFields?.length) {
    return "—";
  }

  const normalizeAddressValue = (value: string): string => {
    const normalizedValue = value.trim();
    return normalizedValue === "-" || normalizedValue === "—"
      ? ""
      : normalizedValue;
  };

  const getFieldValue = (fieldName: keyof Property): string => {
    if (!availableAddressFields.includes(fieldName)) {
      return "";
    }

    return normalizeAddressValue((property[fieldName] as string) || "");
  };

  const addressLine1 = excludeAddressLine1 ? "" : getFieldValue("address1");
  const hasAddressLine2 = availableAddressFields.includes("address2");
  const addressLine2 = hasAddressLine2 ? getFieldValue("address2") : "";

  const stateZip = [getFieldValue("stateProvince"), getFieldValue("postalCode")]
    .filter(Boolean)
    .join(" ");

  const cityStateZipComponent = hasAddressLine2
    ? [addressLine2, getFieldValue("city"), stateZip].filter(Boolean)
    : [getFieldValue("city"), stateZip].filter(Boolean);

  const addressLines = [addressLine1, cityStateZipComponent.join(", ")].filter(
    Boolean,
  );

  return addressLines.length > 0 ? addressLines.join("\n") : "—";
};

/**
 * Performance comparison utility
 * Measures the memory and processing benefits of the optimization
 */
export const compareApproaches = (
  properties: Property[],
  field: ExtendedPropertyFieldRule,
) => {
  // Original approach: Pass entire properties array
  const originalSize = JSON.stringify(properties).length;

  // Optimized approach: Pre-process and pass only values
  const optimizedValues = preprocessPropertyFieldValues(properties, field);
  const optimizedSize = JSON.stringify(optimizedValues).length;

  const memoryReduction = (
    ((originalSize - optimizedSize) / originalSize) *
    100
  ).toFixed(2);

  return {
    originalSize,
    optimizedSize,
    memoryReduction: `${memoryReduction}%`,
    fieldsReduced:
      properties.length > 0 ? Object.keys(properties[0]).length - 3 : 0, // 3 = propertyId, value, isEmpty
  };
};
